import FastImage, { Source } from '@d11/react-native-fast-image';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  RefreshControl,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { mvs } from 'react-native-size-matters';

import { useAppSelector } from '../../app/hooks';
import { CustomIcon } from '../../components/CustomIcon';
import CustomText from '../../components/CustomText';
import {
  EpisodeCard,
  HTMLContentRenderer,
  PodcastHeaderTablet,
  PodcastMeta,
} from '../../components/Podcast';
import { ScreenLayout } from '../../components/ScreenLayout';
import { COLORS, DataState, IS_WEB, isTablet } from '../../constants';
import {
  selectDefaultCover,
  selectDivRadius,
} from '../../features/appconfig/store/selectors';
import { useAdjustedFontSize } from '../../hooks/useAdjustedFontSize';
import { usePodcastFeedData } from '../../hooks/usePodcastFeedData';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import i18n from '../../localization/i18n';
import { PodcastEpisodeModel } from '../../models';
import { PodcastStackNavigatorParams } from '../../navigation/PodcastStackNavigator';

type PodcastFeedScreenProps = NativeStackScreenProps<
  PodcastStackNavigatorParams,
  'PodcastFeed'
>;

// list item separator
const ItemSeparator = () => (
  <View style={styles.listSeparator}>
    <View style={styles.listSeparatorLine} />
  </View>
);

export default function PodcastFeedScreen({ route, navigation }: PodcastFeedScreenProps) {
  const { podcastUrl, tabName, podcastId } = route.params;

  // fetch podcast episodes
  const {
    podcastFeedsContent,
    podcastFeedsContentState,
    currentPage,
    lastPage,
    toggleOrder,
    refetch,
    loadNextPage,
  } = usePodcastFeedData({
    podcastUrl,
    tabName,
    podcastId,
  });

  const [refreshing, setRefreshing] = useState(false);

  const defaultCover = useAppSelector(selectDefaultCover);

  // applies custom layout config
  const divRadius = useAppSelector(selectDivRadius);
  const { bodyHorizontalPadding } = useResponsiveLayout();

  // dynamic font sizes
  const podcastTitleFontSize = useAdjustedFontSize(mvs(24));
  const hostsFontSize = useAdjustedFontSize(mvs(12));
  const sectionTitleFontSize = useAdjustedFontSize(mvs(20));

  const handleBackPress = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const handleEpisodePress = useCallback(
    (episode: PodcastEpisodeModel) => {
      navigation.navigate('PodcastEpisode', {
        episode,
      });
    },
    [navigation],
  );

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  }, [refetch]);

  const handleLoadNextPage = useCallback(() => {
    if (currentPage < lastPage) {
      loadNextPage();
    }
  }, [currentPage, lastPage, loadNextPage]);

  // show loader when loading or when we don't have data yet (initial load only)
  const isInitialLoading =
    (podcastFeedsContentState === DataState.LOADING ||
      podcastFeedsContentState === DataState.NOT_LOADED) &&
    !podcastFeedsContent;

  // show loader when loading next page
  const isLoadingNextPage =
    podcastFeedsContentState === DataState.LOADING && podcastFeedsContent;

  const renderEpisode = useCallback(
    ({ item }: { item: PodcastEpisodeModel }) => (
      <View style={styles.itemContainer}>
        <EpisodeCard
          episode={item}
          isNew={false}
          onPress={() => handleEpisodePress(item)}
        />
      </View>
    ),
    [handleEpisodePress],
  );

  const renderFooter = useCallback(() => {
    return (
      <>
        {isLoadingNextPage && (
          <View style={[styles.footerLoader]}>
            <ActivityIndicator size="small" color={COLORS.BLACK} />
          </View>
        )}
        <View
          style={[
            styles.panelFooter,
            {
              borderBottomLeftRadius: divRadius,
              borderBottomRightRadius: divRadius,
            },
          ]}
        />
      </>
    );
  }, [isLoadingNextPage, divRadius]);

  // stable key extractor
  const keyExtractor = useCallback(
    (item: PodcastEpisodeModel) => item.id || item.url,
    [],
  );

  // memoized list header, avoids recreating a heavy tree on each render
  const listHeader = useMemo(
    () => (
      <View>
        {/* podcast header mobile */}
        {!isTablet && (
          <View style={styles.podcastHeader}>
            {/* podcast thumbnail */}
            <View style={[styles.thumbnailContainer, { borderRadius: divRadius }]}>
              <FastImage
                source={
                  (podcastFeedsContent?.logo
                    ? { uri: podcastFeedsContent?.logo }
                    : defaultCover) as Source | number
                }
                style={styles.thumbnail}
                resizeMode={FastImage.resizeMode.cover}
              />
            </View>

            {/* podcast info */}
            <View style={styles.podcastInfo}>
              {/* podcast title */}
              <CustomText
                style={[
                  styles.podcastTitle,
                  {
                    fontSize: podcastTitleFontSize,
                  },
                ]}
                weight="Bold"
                numberOfLines={2}
              >
                {podcastFeedsContent?.title || ''}
              </CustomText>

              {/* podcast description */}
              <View style={styles.description}>
                <HTMLContentRenderer
                  content={podcastFeedsContent?.description || ''}
                  opacity={0.7}
                  textColor={COLORS.WHITE}
                  textAlign={'center'}
                />
              </View>

              {/* author */}
              {podcastFeedsContent?.author && (
                <CustomText
                  style={[
                    styles.hosts,
                    {
                      fontSize: hostsFontSize,
                    },
                  ]}
                >
                  {podcastFeedsContent.author}
                </CustomText>
              )}
            </View>

            {/* podcast meta info */}
            <PodcastMeta podcast={podcastFeedsContent} />
          </View>
        )}

        {/* podcast header tablet */}
        {isTablet && <PodcastHeaderTablet podcastContent={podcastFeedsContent} />}

        {/* episodes section panel header (white, rounded top) */}
        <View
          style={[
            styles.panelHeader,
            { borderTopLeftRadius: divRadius, borderTopRightRadius: divRadius },
          ]}
        >
          <View style={styles.episodesHeaderRow}>
            <CustomText
              style={[
                styles.episodesTitle,
                {
                  fontSize: sectionTitleFontSize,
                },
              ]}
              weight="Bold"
            >
              {i18n.t('podcast.episodes')}
            </CustomText>

            {podcastFeedsContentState === DataState.LOADING ? (
              <ActivityIndicator
                size="small"
                color={COLORS.BLACK}
                style={styles.sortButton}
              />
            ) : (
              <CustomIcon
                size={16}
                color={COLORS.BLACK}
                name="sort"
                style={styles.sortButton}
                onPress={toggleOrder}
              />
            )}
          </View>
        </View>
      </View>
    ),
    [
      divRadius,
      podcastFeedsContent,
      podcastFeedsContentState,
      defaultCover,
      podcastTitleFontSize,
      hostsFontSize,
      sectionTitleFontSize,
      toggleOrder,
    ],
  );

  if (isInitialLoading) {
    return (
      <ScreenLayout
        isPodcastModule
        headerContents={
          <View style={[styles.header]}>
            <TouchableOpacity onPress={handleBackPress}>
              <CustomIcon name="back" size={20} color={COLORS.WHITE} />
            </TouchableOpacity>
          </View>
        }
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.WHITE} />
        </View>
      </ScreenLayout>
    );
  }

  // error state
  if (podcastFeedsContentState === DataState.ERROR) {
    return (
      <ScreenLayout
        isPodcastModule
        headerContents={
          <View style={[styles.header]}>
            <TouchableOpacity onPress={handleBackPress}>
              <CustomIcon name="back" size={20} color={COLORS.WHITE} />
            </TouchableOpacity>
          </View>
        }
      >
        <View style={styles.errorContainer}>
          <CustomText style={styles.errorText}>{i18n.t('podcast.notFound')}</CustomText>
        </View>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout
      isPodcastModule
      headerContents={
        <View style={[styles.header]}>
          <TouchableOpacity onPress={handleBackPress}>
            <CustomIcon name="back" size={20} color={COLORS.WHITE} />
          </TouchableOpacity>
        </View>
      }
    >
      <FlatList
        style={[styles.container]}
        contentContainerStyle={[{ paddingHorizontal: bodyHorizontalPadding }]}
        showsVerticalScrollIndicator={false}
        data={podcastFeedsContent?.data || []}
        renderItem={renderEpisode}
        keyExtractor={keyExtractor}
        onEndReached={handleLoadNextPage}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        ItemSeparatorComponent={ItemSeparator}
        ListHeaderComponent={listHeader}
        initialNumToRender={2}
        maxToRenderPerBatch={4}
        windowSize={5}
        removeClippedSubviews={true}
        ListFooterComponent={renderFooter}
      />
    </ScreenLayout>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  container: {
    flex: 1,
    paddingBottom: IS_WEB ? 0 : mvs(32),
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontSize: mvs(16),
    color: COLORS.WHITE,
  },
  podcastHeader: {
    alignItems: 'center',
    marginBottom: mvs(16),
    gap: mvs(32),
  },
  description: {
    width: mvs(300),
    marginTop: mvs(4),
  },
  thumbnailContainer: {
    width: mvs(246),
    height: mvs(246),
    overflow: 'hidden',
  },
  thumbnail: {
    width: '100%',
    height: '100%',
  },
  podcastInfo: {
    alignItems: 'center',
    width: '100%',
  },
  podcastTitle: {
    textAlign: 'center',
    marginBottom: mvs(4),
    color: COLORS.WHITE,
  },
  hosts: {
    textAlign: 'center',
    color: COLORS.WHITE,
  },
  itemContainer: {
    backgroundColor: COLORS.WHITE,
    paddingHorizontal: mvs(16),
  },
  listSeparator: {
    backgroundColor: COLORS.WHITE,
    width: '100%',
    height: StyleSheet.hairlineWidth,
  },
  listSeparatorLine: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: 'rgba(37, 37, 37, 0.2)',
    marginHorizontal: mvs(16),
  },
  panelHeader: {
    backgroundColor: COLORS.WHITE,
    paddingHorizontal: mvs(16),
    paddingTop: mvs(16),
    paddingBottom: mvs(10),
  },
  episodesHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  episodesTitle: {
    flex: 1,
  },
  sortButton: {
    marginLeft: mvs(8),
    transform: [{ rotate: '270deg' }],
    opacity: 0.7,
  },
  footerLoader: {
    paddingTop: mvs(16),
    alignItems: 'center',
    backgroundColor: COLORS.WHITE,
  },
  panelFooter: {
    height: mvs(16),
    backgroundColor: COLORS.WHITE,
  },
});
